import { motion } from "framer-motion";
import { useState } from "react";
import {
  FaReact, FaLaravel, FaPython, FaDocker, FaGitAlt, 
  FaUserTie, FaLightbulb, FaRocket
} from "react-icons/fa";
import {
  SiDjango, SiTailwindcss, SiMysql,  SiJavascript,
  SiSqlite,
} from "react-icons/si";

const techStack = [
  { icon: <FaReact className="text-2xl" />, name: "React", color: "#61DAFB", category: "frontend" },
  { icon: <SiJavascript className="text-2xl" />, name: "JavaScript", color: "#F7DF1E", category: "frontend" },
  { icon: <SiTailwindcss className="text-2xl" />, name: "Tailwind CSS", color: "#38BDF8", category: "frontend" },
  { icon: <FaLaravel className="text-2xl" />, name: "Laravel", color: "#F9322C", category: "backend" },
  { icon: <SiDjango className="text-2xl" />, name: "Django", color: " #77D15C", category: "backend" },
  { icon: <FaPython className="text-2xl" />, name: "Python", color: "#3776AB", category: "backend" },
  { icon: <SiMysql className="text-2xl" />, name: "MySQL", color: "#4479A1", category: "database" },
  { icon: <SiSqlite className="text-2xl" />, name: "Sqlite", color: "#4479A1", category: "database" },
  { icon: <FaDocker className="text-2xl" />, name: "Dockers", color: "#0EA5E9", category: "devops" },
  { icon: <FaGitAlt className="text-2xl" />, name: "Git & CI/CD", color: "#F1502F", category: "devops" },
];

const About = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", label: "All Technologies" },
    { id: "frontend", label: "Frontend" },
    { id: "backend", label: "Backend" },
    { id: "database", label: "Database" },
    { id: "devops", label: "DevOps" }
  ];

  const filteredTech = activeCategory === "all"
    ? techStack
    : techStack.filter(tech => tech.category === activeCategory);

  return (
    <section id="about" className="py-16 relative  dark:bg-gray-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 z-0"></div>
      <div className="absolute top-40 right-0 w-72 h-72 bg-purple-300 dark:bg-purple-900 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-0 left-20 w-96 h-96 bg-blue-300 dark:bg-blue-900 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-40 left-40 w-72 h-72 bg-pink-300 dark:bg-pink-900 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">

          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-6"
          >
            <span className="bg-clip-text font-montserrat text-transparent bg-gradient-to-r from-purple-600 to-pink-600">
              About Me
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-700 font-mono dark:text-gray-300 max-w-3xl mx-auto text-justify leading-relaxed"
          >
            I am a Full Stack Web Developer skilled in developing user friendly websites and applications. 
            These are the areas where I work on creating propositions that work on all devices. I use up-to-date tools, everything from online stores to booking systems, to ensure the projects are reliable and high functioning. 
            Take a look to my portfolio to see how I can assist you with your upcoming project!
          </motion.p>
        </div>

        {/* Profile Section */}
        <div className="grid lg:grid-cols-3 gap-12 mb-24">
          {/* Left Column - Profile Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="aspect-square bg-gradient-to-tr from-purple-600 to-pink-500 rounded-3xl p-1.5 shadow-xl">
              <div className="w-full h-full bg-gray-100 dark:bg-gray-800 rounded-2xl overflow-hidden">
                {/* Replace with your actual image */}
                <div className="w-full h-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-8xl">👨‍💻</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Professional Overview */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:col-span-2 flex flex-col justify-center"
          >
            <h3 className="text-3xl font-montserrat font-bold text-gray-900 dark:text-white mb-6">Professional Overview</h3>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center text-purple-600 dark:text-purple-400">
                    <FaUserTie />
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-robotoMono font-semibold text-gray-900 dark:text-white mb-2">Full Stack Expertise</h4>
                  <p className="text-gray-700 font-mono text-justify dark:text-gray-200">
                  Having 5+ years of practical experience, I have gained a thorough understanding of both frontend and backend technologies. 
                  I then leverage that knowledge to create feasibility studies that weave together technical mentor programs for youth that inspire growth through creativity and individual contributions.


                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/50 flex items-center justify-center text-pink-600 dark:text-pink-400">
                    <FaLightbulb />
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-robotoMono  font-semibold text-gray-900 dark:text-white mb-2">Problem-Solving Approach</h4>
                  <p className="text-gray-700 font-mono text-justify dark:text-gray-200">
                  I enjoy tackling hard problems and finding elegant solutions. 
                  I practice clean code, performance tuning, and system-building practices that will allow your system to grow as your business grows.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="mt-1 flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <FaRocket />
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold font-robotoMono text-gray-900 dark:text-white mb-2">Development Philosophy</h4>
                  <p className="text-gray-700 font-mono text-justify dark:text-gray-200">
                  I prefer to build apps that not only work for now but also are structured to offer the ability to be flexible for the future.
                   I care about purposeful, maintainable codebases that offer long-term value.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tech Stack Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative bg-white dark:bg-gray-800/80 rounded-3xl shadow-xl p-10 border border-gray-100 dark:border-gray-700"
        >
          <h3 className="text-3xl font-montserrat font-bold text-gray-900 dark:text-white mb-8 text-center">Technology Stack</h3>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-robotoMono font-medium transition-all ${activeCategory === category.id
                    ? "bg-purple-600 text-white shadow-md shadow-purple-500/20"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Tech Icons Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {filteredTech.map((tech, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.1 * (i % 5) }}
                viewport={{ once: true }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group relative bg-gray-50 dark:bg-gray-800 rounded-xl p-6 flex flex-col items-center justify-center shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
                style={{
                  backgroundImage: `radial-gradient(circle at center, ${tech.color}10, transparent 70%)`
                }}
              >
                <div className="mb-3 text-3xl" style={{ color: tech.color }}>
                  {tech.icon}
                </div>
                <p className="text-sm font-medium text-gray-800 font-mono dark:text-gray-200">{tech.name}</p>
                <div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 bg-gradient-to-br"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${tech.color}30, transparent 70%)`,
                    transition: "opacity 0.3s ease"
                  }}
                ></div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;